import {Component, OnDestroy, OnInit, signal} from '@angular/core';
import {
  CartegraphClassFilter,
  CartegraphConfiguration,
  CartegraphFeature,
  CartegraphFiltersSettings,
  CgDatasetClassField
} from '../../../../../shared/models/cartegraph.model';
import {ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {CartegraphManagementService} from '../../../../../data/cartegraph/cartegraph-management.service';
import {Router} from '@angular/router';
import {debounceTime} from 'rxjs/operators';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {CardComponent} from '../../../../../shared/components/card/card.component';
import {MatError, MatFormField, MatInput} from '@angular/material/input';
import {MatIcon} from '@angular/material/icon';
import {NgClass, NgTemplateOutlet} from '@angular/common';
import {RowWithSelectComponent} from '../../row-with-select/row-with-select.component';
import {CardContentDirective} from '../../../../../shared/components/card/card-content.directive';
import {MatButton} from '@angular/material/button';
import {CartegraphSettingsImportComponent} from '../cartegraph-import/cartegraph-settings-import.component';
import {Subscription} from 'rxjs';

@Component({
  selector: 'app-cartegraph-settings-import-filters',
  templateUrl: './cartegraph-settings-import-filters.component.html',
  styleUrls: ['../../../settings-fields.scss',
    '../../../settings-common.scss',
  ],
  imports: [
    MatProgressSpinner,
    CardComponent,
    ReactiveFormsModule,
    MatFormField,
    MatIcon,
    NgClass,
    MatError,
    RowWithSelectComponent,
    CardContentDirective,
    MatButton,
    NgTemplateOutlet,
    MatInput,
    CartegraphSettingsImportComponent
  ],
  standalone: true
})
export class CartegraphSettingsImportFiltersComponent implements OnInit, OnDestroy {

  private configuration: CartegraphConfiguration;
  filterSettings: CartegraphFiltersSettings;

  isLoading = signal(true);
  isSaving = false;
  isTesting = false;
  uiError: string;
  form: UntypedFormGroup;
  equipmentClassFields: CgDatasetClassField[];
  filterResults: string[];


  filters: {
    controlName: string,
    title: string,
    placeholder: string,
    required?: boolean,
    hasTest?: boolean,
  }[] = [{
    controlName: 'driverFilter',
    title: 'Drivers import filter',
    placeholder: 'Driver filter e.g. Department in (\'Parks\', \'Streets\')',
    required: true, hasTest: true
  },
    {
      controlName: 'vehicleFilter',
      title: 'Vehicle import filter',
      placeholder: 'Vehicle filter e.g. Description in (\'SUV\', \'Pickup\')',
      required: true, hasTest: true
    }
    ,
    {
      controlName: 'vehicleEquipmentFilter',
      title: 'Vehicle equipment import filter',
      placeholder: 'Attachment filter e.g. Department in ("\'"Parks\', \'Streets\')',
      required: true, hasTest: true
    },
    {
      controlName: 'activityFilter',
      title: 'Activity import filter',
      placeholder: 'Activity filter e.g. Department in (\'Parks\', \'Streets\')',
      required: true, hasTest: true
    },
    {
      controlName: 'materialFilter',
      title: 'Material import filter',
      placeholder: 'Material Filter',
      required: true, hasTest: true
    }
  ];

  private subscriptions: Subscription[] = [];

  constructor(private fb: UntypedFormBuilder,
              private cartegraphManagementService: CartegraphManagementService,
              private router: Router) {
  }

  ngOnInit(): void {
    this.initData().then(() => {
      if (this.hasWorkOrdersFeature()) {
        this.filters.push({
          controlName: 'workOrderFilter',
          title: 'Work Order Filter',
          placeholder: 'Work Order filter e.g. Department in (\'Parks\', \'Streets\')',
          required: true, hasTest: true
        });
      }

      if (this.hasMaterialLocationFeature()) {
        this.filters.push({
          controlName: 'materialLocationFilter',
          title: 'Material locations filter',
          placeholder: 'Material locations filter',
          required: true, hasTest: true
        });
      }

      if (this.hasMaterialMatchingFeature()) {
        this.filters.push({
          controlName: 'materialMatchingFields',
          title: 'Material matching fields',
          placeholder: 'SnowRemovalOpsField,SprayingOpsField,HaulingOpsField,SweepingOpsField',
        });
      }

      this.filterSettings = this.configuration.settings?.import?.filter ?? new CartegraphFiltersSettings();
      this.filterSettings.classFilters = this.configuration.settings?.import?.filter?.classFilters ?? [];
      this.initForm();

    }).catch(() => {
      console.error('Error fetching filter options');
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  private initForm() {

    this.form = this.fb.group({
      driverFilter: new UntypedFormControl(this.filterSettings.importDriverFilter),
      vehicleFilter: new UntypedFormControl(this.filterSettings.importVehicleFilter),
      vehicleEquipmentFilter: new UntypedFormControl(this.filterSettings.importVehicleEquipmentFilter),
      vehicleGroupField: new UntypedFormControl(this.filterSettings.importVehicleGroupField),
      workOrderFilter: new UntypedFormControl(this.filterSettings.importWorkOrderFilter),
      materialMatchingFields: new UntypedFormControl(this.filterSettings.materialMatchingFields),
      activityFilter: new UntypedFormControl(this.filterSettings.classFilters.find(f => f.cgClassName === 'cgActivitiesClass')?.filter),
      materialFilter: new UntypedFormControl(this.filterSettings.classFilters.find(f => f.cgClassName === 'cgMaterialsClass')?.filter),
      materialLocationFilter: new UntypedFormControl(
        this.filterSettings.classFilters.find(f => f.cgClassName === 'cgMaterialLocationsClass')?.filter
      ),
    });

    const driveSub = this.form.controls['driverFilter'].valueChanges.pipe(debounceTime(1000)).subscribe(value => {
      this.filterSettings.importDriverFilter = this.isBlank(value) ? null : value.trim();
      this.saveFilters();
    });
    this.subscriptions.push(driveSub);

    const vehSub = this.form.controls['vehicleFilter'].valueChanges.pipe(debounceTime(1000)).subscribe(value => {
      this.filterSettings.importVehicleFilter = this.isBlank(value) ? null : value.trim();
      this.saveFilters();
    });
    this.subscriptions.push(vehSub);

    const eqSub = this.form.controls['vehicleEquipmentFilter'].valueChanges.pipe(debounceTime(1000)).subscribe(value => {
      this.filterSettings.importVehicleEquipmentFilter = this.isBlank(value) ? null : value.trim();
      this.saveFilters();
    });
    this.subscriptions.push(eqSub);

    const vgSub = this.form.controls['vehicleGroupField'].valueChanges.pipe(debounceTime(1000)).subscribe(value => {
      this.filterSettings.importVehicleGroupField = this.isBlank(value) ? null : value.trim();
      this.saveFilters();
    });
    this.subscriptions.push(vgSub);

    const woSub = this.form.controls['workOrderFilter'].valueChanges.pipe(debounceTime(1000)).subscribe(value => {
      this.filterSettings.importWorkOrderFilter = this.isBlank(value) ? null : value.trim();
      this.saveFilters();
    });
    this.subscriptions.push(woSub);

    const matchSub = this.form.controls['materialMatchingFields'].valueChanges.pipe(debounceTime(1000)).subscribe(value => {
      this.filterSettings.materialMatchingFields = this.isBlank(value) ? null : value.trim();
      this.saveFilters();
    });
    this.subscriptions.push(matchSub);

    const actSub = this.form.controls['activityFilter'].valueChanges.pipe(debounceTime(1000)).subscribe(value => {
      const index = this.filterSettings.classFilters.findIndex(f => f.cgClassName === 'cgActivitiesClass');
      if (index >= 0) {
        this.filterSettings.classFilters.splice(index, 1);
      }
      if (!this.isBlank(value)) {
        this.filterSettings.classFilters.push(new CartegraphClassFilter('cgActivitiesClass', value));
      }
      this.saveFilters();
    });
    this.subscriptions.push(actSub);

    const matSub = this.form.controls['materialFilter'].valueChanges.pipe(debounceTime(1000)).subscribe(value => {
      const index = this.filterSettings.classFilters.findIndex(f => f.cgClassName === 'cgMaterialsClass');
      if (index >= 0) {
        this.filterSettings.classFilters.splice(index, 1);
      }
      if (!this.isBlank(value)) {
        this.filterSettings.classFilters.push(new CartegraphClassFilter('cgMaterialsClass', value));
      }
      this.saveFilters();
    });
    this.subscriptions.push(matSub);

    const locSub = this.form.controls['materialLocationFilter'].valueChanges.pipe(debounceTime(1000)).subscribe(value => {
      const index = this.filterSettings.classFilters.findIndex(f => f.cgClassName === 'cgMaterialLocationsClass');
      if (index >= 0) {
        this.filterSettings.classFilters.splice(index, 1);
      }
      if (!this.isBlank(value)) {
        this.filterSettings.classFilters.push(new CartegraphClassFilter('cgMaterialLocationsClass', value));
      }
      this.saveFilters();
    });
    this.subscriptions.push(locSub);
  }


  private initData(): Promise<any> {
      this.isLoading.set(true);
      return Promise.all([
        this.cartegraphManagementService.getConfiguration(),
        this.cartegraphManagementService.getDatasetClassFields([{dataClass: 'cgEquipmentClass'}])
      ]).then(result => {
        const [cfg, dataset] = result;
        if (cfg.error) {
          this.uiError = cfg.error;
        } else {
          this.configuration = cfg.data ?? new CartegraphConfiguration();
        }
        this.equipmentClassFields = dataset.data;
      }).catch((error) => {
        console.error(error);
        this.uiError = error;
      }).finally(() => {
        this.isLoading.set(false);
      });
  }

  isBlank = (val: string | null | undefined): boolean => {
    return !val || val.trim().length === 0;
  };

  private testBegin() {
    this.isTesting = true;
    this.uiError = null;
    this.filterResults = null;
    this.form.disable({emitEvent: false});
  }

  private testEnd() {
    this.isTesting = false;
    this.form.enable({emitEvent: false});
  }

  onTestClick(controlName: string) {
    switch (controlName) {
      case 'driverFilter':
        this.testLaborFilter(controlName);
        break;
      case 'vehicleFilter':
        this.testVehicleFilter(controlName);
        break;
      case 'vehicleEquipmentFilter':
        this.testVehicleEquipmentFilter(controlName);
        break;
      case 'activityFilter':
        this.testActivityFilter(controlName);
        break;
      case 'workOrderFilter':
        this.testWorkOrderFilter(controlName);
        break;
      case 'materialFilter':
        this.testMaterialFilter(controlName);
        break;
      case 'materialLocationFilter':
        this.testMaterialLocationFilter(controlName);
        break;
      case 'materialMatchingFields':
        // this.test(controlName);
        break;
    }
  }

  private testLaborFilter(controlName: string) {
    this.testBegin();
    const value = this.isBlank(this.form.controls[controlName].value) ? null : this.form.controls[controlName].value;
    this.cartegraphManagementService.fetchLabor(value)
      .then(result => {
        if (result.error) {
          this.uiError = result.error;
        } else {
          this.filterResults = result?.data?.map(v => {
            return `${v.IDField}, ${v.FullNameField}, ${v.EmailAddressField}, ${v.DepartmentField}`;
          });
        }
      }).catch(() => {
    }).finally(() => {
      this.testEnd();
    });
  }

  private testVehicleFilter(controlName: string) {
    this.testBegin();
    const value = this.isBlank(this.form.controls[controlName].value) ? null : this.form.controls[controlName].value;
    this.cartegraphManagementService.fetchEquipment(value)
      .then(result => {
        if (result.error) {
          this.uiError = result.error;
        } else {
          this.filterResults = result?.data?.map(v => {
            return `${v.IDField}, ${v.ManufacturerField}, ${v.ModelField}, ${v.SerialVINNumberField}, ${v.ClassificationField}, ${v.DepartmentField}`;
          });
        }
      }).catch(() => {
        console.warn('Error fetching equipment data');
    }).finally(() => {
      this.testEnd();
    });
  }

  private testVehicleEquipmentFilter(controlName: string) {
    this.testBegin();
    const value = this.isBlank(this.form.controls[controlName].value) ? null : this.form.controls[controlName].value;
    this.cartegraphManagementService.fetchEquipment(value)
      .then(result => {
        if (result.error) {
          this.uiError = result.error;
        } else {
          this.filterResults = result?.data?.map(v => {
            return `${v.IDField}, ${v.ManufacturerField}, ${v.ModelField}, ${v.SerialVINNumberField}, ${v.ClassificationField}, ${v.DepartmentField}`;
          }) || [];
        }
      }).catch(() => {
      console.warn('Error fetching vehicle equipment filter option');
    }).finally(() => {
      this.testEnd();
    });
  }

  private testWorkOrderFilter(controlName: string) {
    this.testBegin();
    const value = this.isBlank(this.form.controls[controlName].value) ? null : this.form.controls[controlName].value;
    this.cartegraphManagementService.fetchBaseObject('cgWorkOrdersClass', value)
      .then(result => {
        if (result.error) {
          this.uiError = result.error;
        } else {
          this.filterResults = result?.data?.map(v => {
            return `${v['IDField']} (${v['DepartmentField']}, ${v['StatusField']})`;
          });

        }
      }).catch(() => {
        console.warn('Error fetching work order filter options');
    }).finally(() => {
      this.testEnd();
    });
  }

  private testActivityFilter(controlName: string) {
    this.testBegin();
    const filter = this.isBlank(this.form.controls[controlName].value) ? null : this.form.controls[controlName].value;
    this.cartegraphManagementService.fetchBaseObject('cgActivitiesClass', filter)
      .then(result => {
        if (result.error) {
          this.uiError = result.error;
        } else {
          this.filterResults = result?.data?.map(v => {
            return `${v['ActivityField']}`;
          });
        }
      }).catch(() => {
        console.warn('Error fetching activity filter options');
    }).finally(() => {
      this.testEnd();
    });
  }

  private testMaterialFilter(controlName: string) {
    this.testBegin();
    const filter = this.isBlank(this.form.controls[controlName].value) ? null : this.form.controls[controlName].value;
    this.cartegraphManagementService.fetchBaseObject('cgMaterialsClass', filter)
      .then(result => {
        if (result.error) {
          this.uiError = result.error;
        } else {
          this.filterResults = result?.data?.map(v => {
            return `${v['IDField']} - ${v['DescriptionField']}`;
          });

        }
      }).catch(() => {
        console.warn('Error fetching material filter options');
    }).finally(() => {
      this.testEnd();
    });
  }

  private testMaterialLocationFilter(controlName: string) {
    this.testBegin();
    this.filterResults = null;
    const filter = this.isBlank(this.form.controls[controlName].value) ? null : this.form.controls[controlName].value;
    this.cartegraphManagementService.fetchBaseObject('cgMaterialLocationsClass', filter)
      .then(result => {
        if (result.error) {
          this.uiError = result.error;
        } else {
          this.filterResults = result?.data?.map(v => {
            return `${v['IDField']} - ${v['DescriptionField']}`;
          });
        }
      }).catch(() => {
        console.warn('Error fetching material location filter options');
    }).finally(() => {
      this.testEnd();
    });
  }

  private hasWorkOrdersFeature(): boolean {
    return this.hasFeature(CartegraphFeature.IMPORT_WORK_ORDERS);
  }

  private hasMaterialMatchingFeature(): boolean {
    return this.hasFeature(CartegraphFeature.USE_MATERIAL_MATCHING);
  }

  private hasMaterialLocationFeature(): boolean {
    return this.hasFeature(CartegraphFeature.USE_MATERIAL_LOCATION);
  }

  private hasFeature(feature: CartegraphFeature): boolean {
    const found = this.configuration.settings?.features?.find(
      f => f.feature === feature
    );
    return found?.enabled ?? false;
  }


  private saveFilters() {
    this.uiError = null;
    this.isSaving = true;
    this.cartegraphManagementService.updateFilters(this.filterSettings).then(response => {
      if (!!response.error) {
        this.uiError = response.error;
      } else {
        if (!!response.data && !!response.data.settings) {
          this.configuration.settings.import.filter = response.data.settings.import.filter;
          this.filterSettings = response.data.settings.import.filter;
        }
      }
    }).catch(error => {
      console.error(error);
      this.uiError = error;
      this.isSaving = false;
    }).finally(() => {
      this.isSaving = false;
    });
  }

  navigateBack() {
    this.router.navigate(['settings', 'cartegraph-settings', 'settings']).then();
  }

}
